import { useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  BarStackChart,
  formatData,
  getGridColorsForCount,
  Layout,
  Legend,
  tooltipFormatter,
  truncateTextWithEllipsis,
} from '@utilities';

import { DISPLAY_AS } from '../../utilities/helpers';
import GridTable from '../GridTable';

import styles from './_index.module.scss';

const Grid = ({
  compareByGroups,
  data,
  displayAs = DISPLAY_AS.PERCENT.value,
  onTop2BoxApplied,
  question,
  selectedCompareBy,
  surveyId,
}) => {
  const isDisplayAsPercent = displayAs === DISPLAY_AS.PERCENT.value;
  const chartKeyToDisplay = isDisplayAsPercent ? 'chartPct' : 'chartCount';
  const tableToDisplay = isDisplayAsPercent ? data?.tablePct : data?.tableCount;

  const colors = useMemo(() => {
    if (!data) return [];
    return getGridColorsForCount(data.segments?.[0]?.chartCount?.series?.length);
  }, [data]);

  return (
    <Layout.Flex.Column className={styles['grid']} gap="x-large">
      <Layout.Flex.Column gap="small">
        <p className={styles['grid-label']}>{`Answer Options by ${
          isDisplayAsPercent ? DISPLAY_AS.PERCENT.label : DISPLAY_AS.COUNT.label
        }`}</p>
        {data?.segments?.map((segment, index) => {
          const chartToDisplay = segment[chartKeyToDisplay];
          const isLastSegment = index === data.segments.length - 1;

          const barHeight = 16;
          const heightForLegend = 32;
          const minHeightForLabel = 20;
          const minHeightForTooltip = 50;
          const paddingBetweenBars = (minHeightForLabel - barHeight) / 2;
          const chartHeight =
            chartToDisplay.xAxis.categories.length * (minHeightForLabel + paddingBetweenBars);
          return (
            <BarStackChart
              chart={{
                height: isLastSegment
                  ? Math.max(chartHeight + heightForLegend, minHeightForTooltip + heightForLegend)
                  : Math.max(chartHeight, minHeightForTooltip),
                marginRight: 15,
                spacing: [0, 0, 0, 0],
              }}
              chartTitle={segment.name}
              className={styles['grid-chart']}
              colors={colors}
              hasLegend={false}
              key={segment.id}
              plotOptions={{
                series: {
                  groupPadding: 0,
                  pointPadding: 0,
                  pointWidth: barHeight,
                },
              }}
              series={chartToDisplay.series}
              tooltip={{
                formatter: function () {
                  return tooltipFormatter({
                    data: this,
                    format: segment[chartKeyToDisplay]?.format,
                  });
                },
              }}
              xAxis={{
                ...segment[chartKeyToDisplay]?.xAxis,
                labels: {
                  align: 'left',
                  formatter: function () {
                    const isOverall = selectedCompareBy?.value === 'overall';
                    const color = isOverall
                      ? compareByGroups?.[0]?.color
                      : compareByGroups?.find(({ id, name }) => [id, name].includes(this.value))
                          ?.color;
                    const text = isOverall
                      ? compareByGroups?.[0]?.name
                      : truncateTextWithEllipsis({ length: 25, text: this.value });
                    return `<div class="${styles['grid-xaxis-label']}"><span class="${styles['grid-xaxis-dot']}" style="background:${color}"></span>${text}</div>`;
                  },
                  reserveSpace: true,
                  useHTML: true,
                },
              }}
              yAxis={{
                labels: {
                  formatter: function () {
                    return isLastSegment
                      ? formatData({
                          format: chartToDisplay.format,
                          value: this.value,
                        })
                      : '';
                  },
                },
                max: isDisplayAsPercent ? 1 : undefined,
                tickAmount: isDisplayAsPercent ? 11 : undefined,
                visible: true,
              }}
            />
          );
        })}
        <Legend
          className={styles['grid-legend']}
          isHorizontal
          items={data.segments?.[0]?.chartCount?.series.map(({ name }, index) => ({
            color: colors[index],
            name,
          }))}
        />
      </Layout.Flex.Column>
      <hr />
      <GridTable
        columns={tableToDisplay?.columns}
        compareByGroups={compareByGroups}
        data={tableToDisplay?.data}
        displayAs={displayAs}
        onTop2BoxApplied={onTop2BoxApplied}
        question={question}
        surveyId={surveyId}
      />
    </Layout.Flex.Column>
  );
};

Grid.propTypes = {
  compareByGroups: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.object,
  displayAs: PropTypes.string,
  onTop2BoxApplied: PropTypes.func,
  question: PropTypes.object,
  selectedCompareBy: PropTypes.object,
  surveyId: PropTypes.number,
};

export default Grid;
